/**
 * Performance monitoring utilities
 * Tracks Core Web Vitals and custom performance metrics
 */

import { logger } from './logger';

// Core Web Vitals thresholds (in milliseconds)
const THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 },
  FID: { good: 100, needsImprovement: 300 },
  CLS: { good: 0.1, needsImprovement: 0.25 },
  FCP: { good: 1800, needsImprovement: 3000 },
  TTFB: { good: 800, needsImprovement: 1800 },
} as const;

interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private observer: PerformanceObserver | null = null;

  /**
   * Initialize performance monitoring
   */
  init(): void {
    if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
      logger.warn('PerformanceObserver not supported');
      return;
    }

    // Monitor Largest Contentful Paint (LCP)
    this.observeLCP();

    // Monitor First Input Delay (FID)
    this.observeFID();

    // Monitor Cumulative Layout Shift (CLS)
    this.observeCLS();

    // Monitor First Contentful Paint (FCP)
    this.observeFCP();

    // Monitor Time to First Byte (TTFB)
    this.observeTTFB();

    // Monitor long tasks
    this.observeLongTasks();

    // Log navigation timing
    this.logNavigationTiming();
  }

  /**
   * Observe Largest Contentful Paint
   */
  private observeLCP(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as PerformanceEntry & { renderTime?: number; loadTime?: number };
        const value = lastEntry.renderTime || lastEntry.loadTime || 0;

        this.recordMetric('LCP', value, this.getRating(value, THRESHOLDS.LCP));
      });

      observer.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (error) {
      logger.error('Error observing LCP:', error);
    }
  }

  /**
   * Observe First Input Delay
   */
  private observeFID(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          const fidEntry = entry as PerformanceEntry & { processingStart?: number };
          const value = fidEntry.processingStart ? fidEntry.processingStart - entry.startTime : 0;
          this.recordMetric('FID', value, this.getRating(value, THRESHOLDS.FID));
        });
      });

      observer.observe({ entryTypes: ['first-input'] });
    } catch (error) {
      logger.error('Error observing FID:', error);
    }
  }

  /**
   * Observe Cumulative Layout Shift
   */
  private observeCLS(): void {
    try {
      let clsValue = 0;
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          const layoutShiftEntry = entry as PerformanceEntry & { hadRecentInput?: boolean; value?: number };
          if (!layoutShiftEntry.hadRecentInput) {
            clsValue += layoutShiftEntry.value || 0;
            this.recordMetric('CLS', clsValue, this.getRating(clsValue, THRESHOLDS.CLS));
          }
        });
      });

      observer.observe({ entryTypes: ['layout-shift'] });
    } catch (error) {
      logger.error('Error observing CLS:', error);
    }
  }

  /**
   * Observe First Contentful Paint
   */
  private observeFCP(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.name === 'first-contentful-paint') {
            this.recordMetric('FCP', entry.startTime, this.getRating(entry.startTime, THRESHOLDS.FCP));
          }
        });
      });

      observer.observe({ entryTypes: ['paint'] });
    } catch (error) {
      logger.error('Error observing FCP:', error);
    }
  }

  /**
   * Observe Time to First Byte
   */
  private observeTTFB(): void {
    try {
      const navigationTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigationTiming) {
        const ttfb = navigationTiming.responseStart - navigationTiming.requestStart;
        this.recordMetric('TTFB', ttfb, this.getRating(ttfb, THRESHOLDS.TTFB));
      }
    } catch (error) {
      logger.error('Error observing TTFB:', error);
    }
  }

  /**
   * Observe long tasks (>50ms)
   */
  private observeLongTasks(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.duration > 50) {
            logger.warn(`Long task detected: ${entry.name} (${entry.duration.toFixed(2)}ms)`);
          }
        });
      });

      observer.observe({ entryTypes: ['longtask'] });
    } catch (error) {
      // longtask not supported in all browsers
      logger.debug('Long task monitoring not supported');
    }
  }

  /**
   * Log navigation timing details
   */
  private logNavigationTiming(): void {
    try {
      const navigationTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigationTiming) {
        logger.info('Navigation timing:', {
          dns: navigationTiming.domainLookupEnd - navigationTiming.domainLookupStart,
          tcp: navigationTiming.connectEnd - navigationTiming.connectStart,
          request: navigationTiming.responseStart - navigationTiming.requestStart,
          response: navigationTiming.responseEnd - navigationTiming.responseStart,
          domProcessing: navigationTiming.domContentLoadedEventEnd - navigationTiming.responseEnd,
          total: navigationTiming.loadEventEnd - navigationTiming.fetchStart,
        });
      }
    } catch (error) {
      logger.error('Error logging navigation timing:', error);
    }
  }

  /**
   * Record a performance metric
   */
  private recordMetric(name: string, value: number, rating: 'good' | 'needs-improvement' | 'poor'): void {
    const metric: PerformanceMetric = {
      name,
      value,
      rating,
      timestamp: Date.now(),
    };

    this.metrics.push(metric);

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      logger.info(`Performance metric: ${name} = ${value.toFixed(2)}ms (${rating})`);
    }

    // Send to analytics in production (if needed)
    if (process.env.NODE_ENV === 'production') {
      this.sendToAnalytics(metric);
    }
  }

  /**
   * Get rating based on threshold
   */
  private getRating(
    value: number,
    threshold: { good: number; needsImprovement: number }
  ): 'good' | 'needs-improvement' | 'poor' {
    if (value <= threshold.good) return 'good';
    if (value <= threshold.needsImprovement) return 'needs-improvement';
    return 'poor';
  }

  /**
   * Send metric to analytics service
   */
  private sendToAnalytics(metric: PerformanceMetric): void {
    // TODO: Implement analytics integration (e.g., Google Analytics, custom endpoint)
    // Example:
    // fetch('/api/analytics/performance', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(metric),
    // });
  }

  /**
   * Get all recorded metrics
   */
  getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  /**
   * Clear all metrics
   */
  clearMetrics(): void {
    this.metrics = [];
  }

  /**
   * Mark a custom performance measure
   */
  mark(name: string): void {
    try {
      performance.mark(name);
    } catch (error) {
      logger.error(`Error marking ${name}:`, error);
    }
  }

  /**
   * Measure time between two marks
   */
  measure(name: string, startMark: string, endMark?: string): void {
    try {
      if (endMark) {
        performance.measure(name, startMark, endMark);
      } else {
        performance.measure(name, startMark);
      }

      const measure = performance.getEntriesByName(name, 'measure')[0];
      if (measure) {
        logger.info(`Custom measure: ${name} = ${measure.duration.toFixed(2)}ms`);
      }
    } catch (error) {
      logger.error(`Error measuring ${name}:`, error);
    }
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Export for use in React components
export const usePerformanceMonitor = () => {
  return performanceMonitor;
};

// Initialize on module load (only in browser)
if (typeof window !== 'undefined') {
  performanceMonitor.init();
}
